<template>
    <Card class="BaseDashboardTable">
        <div class="BaseDashboardTable-header">
            <div class="BaseDashboardTable-title-row">
                <div class="BaseDashboardTable-title">
                    <slot name="title"></slot>
                    <span v-if="showItemCounter" class="item-counter">
                        {{ itemCount }}
                        {{ itemCount === 1 ? 'Eintrag' : 'Einträge' }}
                    </span>
                </div>
                <div class="BaseDashboardTable-actions">
                    <slot name="actions"></slot>
                    <Popover
                        v-if="showColumnToggler"
                        class="column-toggler"
                        speed="normal"
                        horizontal-position="right"
                    >
                        <template #trigger>
                            <button class="column-toggler-button">
                                <i class="ri-settings-line"></i>
                            </button>
                        </template>
                        <template #content="{ close }">
                            <div class="column-toggler-content">
                                <div class="column-toggler-header">Spalten anzeigen</div>
                                <ScrollableContainer class="column-toggler-list" max-height="300px">
                                    <div
                                        v-for="column in columns"
                                        :key="column.key"
                                        class="column-toggler-item"
                                    >
                                        <Checkbox
                                            :value="isColumnVisible(column)"
                                            :disabled="isColumnRequired(column)"
                                            @change="toggleColumnVisibility(column, close)"
                                        >
                                            {{ column.label }}
                                        </Checkbox>
                                    </div>
                                </ScrollableContainer>
                            </div>
                        </template>
                    </Popover>
                    <Button
                        v-if="showExport"
                        variant="plain"
                        @onClick="handleExport('csv')"
                        size="small"
                    >
                        <i class="ri-download-line" style="margin-right: 6px"></i>
                        {{ exportTitle }}
                    </Button>
                </div>
            </div>
            <div
                v-if="hasSearch || (showLegend && legendItems.length > 0)"
                class="search-legend-container"
            >
                <div class="search-legend-left">
                    <div v-if="hasSearch" class="search-container">
                        <i class="ri-search-line search-icon"></i>
                        <input
                            type="text"
                            class="search-input"
                            :placeholder="searchPlaceholder"
                            @input="debouncedSearch($event.target.value)"
                        />
                    </div>
                </div>
                <div v-if="showLegend && legendItems.length > 0" class="BaseDashboardTable-legend">
                    <TableLegend :legend-items="legendItems" />
                </div>
            </div>
        </div>
        <div
            class="BaseDashboardTable-container"
            :class="{ 'no-items': !filteredItems.length }"
            ref="tableContainer"
            :style="{ height: filteredItems.length ? containerHeight : 'auto' }"
        >
            <HoverIndicator variant="glow" ref="tableHoverIndicator" />
            <table
                :style="{ minWidth: isMobile ? 'auto' : tableMinWidth }"
                :class="{ 'hierarchical-table': useHierarchicalRows }"
            >
                <thead>
                    <tr>
                        <th
                            v-if="showCounter"
                            class="counter-column text-center"
                            style="width: 50px; min-width: 50px"
                        >
                            #
                        </th>
                        <th
                            v-for="(column, index) in visibleColumns"
                            :key="index"
                            :data-priority="column.priority || 1"
                            :class="[
                                column.align ? `text-${column.align}` : '',
                                { sortable: column.sortable !== false },
                                { sorted: currentSort.column === column.key },
                                {
                                    'sorted-asc':
                                        currentSort.column === column.key && currentSort.ascending,
                                },
                                {
                                    'sorted-desc':
                                        currentSort.column === column.key && !currentSort.ascending,
                                },
                            ]"
                            :style="getColumnStyle(column)"
                            @click="handleHeaderClick(column)"
                        >
                            <div class="column-header">
                                {{ truncateText(column.label, headerTruncateLength) }}
                                <i
                                    v-if="column.sortable !== false"
                                    :class="[
                                        currentSort && currentSort.column === column.key
                                            ? currentSort.ascending
                                                ? 'ri-arrow-up-s-line active'
                                                : 'ri-arrow-down-s-line active'
                                            : 'ri-arrow-up-down-line',
                                    ]"
                                ></i>
                            </div>
                        </th>

                        <th v-if="selectable" class="checkbox-column">
                            {{ selectColumnLabel }}
                            <input
                                type="checkbox"
                                :checked="allSelected"
                                @change="toggleSelectAll"
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        class="top-spacer"
                        v-if="startIndex > 0"
                        :style="{ height: topSpacerHeight + 'px' }"
                    ></tr>
                    <template v-for="(item, index) in visibleItems">
                        <BaseDashboardTableRow
                            :key="itemKey && item[itemKey] ? item[itemKey] : item._id || index"
                            :item="item"
                            :index="startIndex + index + 1"
                            :data-index="startIndex + index"
                            :show-counter="showCounter"
                            :columns="visibleColumns"
                            :is-clickable="isClickable"
                            :selectable="selectable"
                            :has-any-expandable-rows="hasAnyExpandableRows"
                            :is-checked="isChecked(item)"
                            :expanded="isRowExpanded(item)"
                            :is-selected="isSelected(item)"
                            :cell-truncate-length="cellTruncateLength"
                            :global-components="globalComponents"
                            :search-query="searchQuery"
                            :searchable-columns="searchableColumns"
                            :is-disabled="item.isDisabled"
                            :border-color="getRowBorderColor(item)"
                            @row-click="
                                handleRowClick($event.item, startIndex + $event.index, $event.event)
                            "
                            @row-hover="handleRowHover($event.item, startIndex + $event.index)"
                            @row-leave="handleRowLeave($event.item, startIndex + $event.index)"
                            @toggle-expand="toggleExpand($event.item, startIndex + $event.index)"
                            @item-checked="toggleItemChecked"
                        >
                            <template
                                v-for="column in visibleColumns"
                                #[`column-${column.key}`]="slotProps"
                            >
                                <slot
                                    :name="`column-${column.key}`"
                                    v-bind="{ ...slotProps, item, index: startIndex + index }"
                                ></slot>
                            </template>
                        </BaseDashboardTableRow>

                        <tr
                            v-if="!useHierarchicalRows && isRowExpanded(item)"
                            :key="`${startIndex + index}-expanded`"
                            class="nested-table-row"
                        >
                            <td :colspan="visibleColumns.length">
                                <div class="nested-table-container">
                                    <slot
                                        name="nested-table"
                                        :item="item"
                                        :index="startIndex + index"
                                    ></slot>
                                </div>
                            </td>
                        </tr>
                    </template>

                    <tr
                        class="bottom-spacer"
                        v-if="endIndex < filteredItems.length"
                        :style="{ height: bottomSpacerHeight + 'px' }"
                    ></tr>
                </tbody>
            </table>
        </div>

        <div v-if="!filteredItems.length" class="table-empty">
            <i class="ri-inbox-line"></i>
            <p>{{ searchQuery ? 'Keine Ergebnisse gefunden.' : emptyMessage }}</p>
        </div>
    </Card>
</template>

<script>
import Card from '@/components/Card';
import Checkbox from '@/components/widgets/Checkbox';
import BaseDashboardTableRow from '@/components/BaseDashboardTableRow';
import Button from '@/components/widgets/Button.vue';
import HoverIndicator from '@/components/widgets/HoverIndicator.vue';
import TableLegend from '@/components/TableLegend.vue';
import Popover from '@/components/widgets/Popover.vue';
import ScrollableContainer from '@/components/widgets/ScrollableContainer.vue';
import resizeMixin from '@/mixins/resizeMixin';

export default {
    name: 'BaseDashboardTable',
    mixins: [resizeMixin],
    components: {
        Card,
        Checkbox,
        Button,
        BaseDashboardTableRow,
        HoverIndicator,
        TableLegend,
        Popover,
        ScrollableContainer,
    },
    props: {
        showItemCounter: {
            type: Boolean,
            default: true,
        },
        columns: {
            type: Array,
            required: true,
        },
        items: {
            type: Array,
            default: () => [],
        },
        sort: {
            type: Object,
            default: null,
        },
        cellTruncateLength: {
            type: Number,
            default: 50,
        },
        headerTruncateLength: {
            type: Number,
            default: 20,
        },
        selectedFn: {
            type: Function,
            default: () => false,
        },
        isClickable: {
            type: Boolean,
            default: true,
        },
        emptyMessage: {
            type: String,
            default: 'Noch keine Daten vorhanden.',
        },
        selectable: {
            type: Boolean,
            default: false,
        },
        checkedItems: {
            type: Array,
            default: () => [],
        },
        itemKey: {
            type: String,
            default: null,
        },
        selectAllByDefault: {
            type: Boolean,
            default: false,
        },
        selectColumnLabel: {
            type: String,
            default: 'Akzeptiert',
        },
        globalComponents: {
            type: Object,
            default: () => ({}),
        },
        useHierarchicalRows: {
            type: Boolean,
            default: false,
        },
        containerHeight: {
            type: String,
            default: 'auto',
        },
        hasSearch: {
            type: Boolean,
            default: true,
        },
        searchPlaceholder: {
            type: String,
            default: 'Suchen...',
        },
        searchableColumns: {
            type: Array,
            default: () => [],
        },
        showCounter: {
            type: Boolean,
            default: false,
        },
        itemHeight: {
            type: Number,
            default: 35,
        },
        bufferSize: {
            type: Number,
            default: 30,
        },
        useItemVirtualization: {
            type: Boolean,
            default: false,
        },
        virtualizationThreshold: {
            type: Number,
            default: 25,
        },
        initialSearchQuery: {
            type: String,
            default: '',
        },
        showLegend: {
            type: Boolean,
            default: false,
        },
        legendItems: {
            type: Array,
            default: () => [],
        },
        showExport: {
            type: Boolean,
            default: true,
        },
        exportTitle: {
            type: String,
            default: 'Export CSV',
        },
        tableTitle: {
            type: String,
            default: null,
        },
        pageId: {
            type: String,
            default: null,
        },
        tableId: {
            type: String,
            default: null,
        },
        showColumnToggler: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentSort: { ...this.sort },
            localCheckedItems: [],
            searchQuery: this.initialSearchQuery,
            tableMinWidth: 0,
            debouncedSearch: null,
            startIndex: 0,
            endIndex: 0,
            visibleHeight: 0,
            scrollTop: 0,
            lastScrollTime: 0,
            scrollThrottleTime: 16,
            recentScrollTime: 0,
            hoverDelayAfterScroll: 200,
            columnWidths: {},
            maxColumnWidths: {},
            tableHoverTimeout: null,
            scrollContainer: null,
            extractedTitle: null,
        };
    },
    computed: {
        shouldUseVirtualization() {
            if (!this.useItemVirtualization) {
                return false;
            }

            if (this.isMobile) {
                return (
                    this.filteredItems.length >= this.virtualizationThreshold &&
                    this.scrollContainer
                );
            }

            return this.filteredItems.length >= this.virtualizationThreshold;
        },
        filteredItems() {
            if (!this.hasSearch || !this.searchQuery) {
                return this.items;
            }

            const query = this.searchQuery.toLowerCase();
            const searchableColumns =
                this.searchableColumns.length > 0
                    ? this.searchableColumns
                    : this.columns.map((col) => col.key);

            return this.items.filter((item) => {
                return searchableColumns.some((columnKey) => {
                    const value = this.getItemValue(item, { key: columnKey });
                    return value != null && String(value).toLowerCase().includes(query);
                });
            });
        },
        itemCount() {
            return this.filteredItems.filter((item) => !item.isSeparator).length;
        },
        visibleItems() {
            const shouldVirtualize =
                this.useItemVirtualization &&
                this.filteredItems.length >= this.virtualizationThreshold;

            if (shouldVirtualize && !this.scrollContainer) {
                const safeLimit =
                    Math.ceil(window.innerHeight / this.itemHeight) + this.bufferSize * 2;
                return this.filteredItems.slice(0, safeLimit);
            }

            if (!this.shouldUseVirtualization) {
                return this.filteredItems;
            }

            return this.filteredItems.slice(this.startIndex, this.endIndex);
        },

        topSpacerHeight() {
            return this.shouldUseVirtualization ? this.startIndex * this.itemHeight : 0;
        },

        bottomSpacerHeight() {
            if (!this.shouldUseVirtualization) return 0;

            const remainingItems = this.filteredItems.length - this.endIndex;
            return remainingItems > 0 ? remainingItems * this.itemHeight : 0;
        },
        allSelected() {
            return (
                this.filteredItems.length > 0 &&
                this.filteredItems.every((item) => this.isChecked(item))
            );
        },
        hasAnyExpandableRows() {
            return this.filteredItems.some((item) => this.hasChildren(item));
        },
        tablePreferences() {
            if (!this.tableId) return null;
            return this.$store.getters['tablePreferences/getTablePreferences'](this.tableId);
        },
        visibleColumns() {
            if (!this.showColumnToggler || !this.tablePreferences) {
                return this.columns;
            }

            const visibilityMap = this.tablePreferences.columns?.visibility || {};
            const filteredColumns = this.columns.filter(
                (column) => visibilityMap[column.key] !== false,
            );
            return filteredColumns;
        },
    },
    async created() {
        if (
            this.selectAllByDefault &&
            this.filteredItems.length > 0 &&
            this.localCheckedItems.length === 0
        ) {
            this.localCheckedItems = [...this.filteredItems];
        } else {
            this.localCheckedItems = [...this.checkedItems];
        }

        const debounceDelay = this.items.length > 1000 ? 500 : 300;
        this.debouncedSearch = this.debounce((value) => {
            this.searchQuery = value;
            this.$emit('search-query-changed', value);
        }, debounceDelay);
    },
    watch: {
        filteredItems: {
            handler() {
                this.$nextTick(() => {
                    this.calculateVisibleItems();
                    if (!this.shouldUseVirtualization) {
                        this.addTableRowHoverEvents();
                    }
                });
            },
            deep: false,
        },
        sort: {
            handler(newSort) {
                this.currentSort = { ...newSort };
            },
            deep: true,
        },
        checkedItems: {
            handler(newVal) {
                this.localCheckedItems = [...newVal];
            },
            deep: true,
        },
        items: {
            handler(newItems) {
                if (
                    this.selectAllByDefault &&
                    newItems.length > 0 &&
                    this.localCheckedItems.length === 0
                ) {
                    this.localCheckedItems = [...newItems];
                    this.$emit('checked-changed', this.localCheckedItems);
                }

                if (newItems.length <= 1000) {
                    this.$nextTick(() => {
                        this.calculateColumnWidths();
                    });
                }
            },
            immediate: true,
        },
        visibleItems: {
            handler() {
                this.$nextTick(() => {
                    this.addTableRowHoverEvents();
                });
            },
            deep: false,
        },
        searchQuery(newVal, oldVal) {
            if (oldVal && !newVal) {
                this.$emit('search-cleared');
            }
        },
        initialSearchQuery: {
            handler(newVal) {
                this.searchQuery = newVal;
                this.$nextTick(() => {
                    const searchInput = this.$el.querySelector('.search-input');
                    if (searchInput) {
                        searchInput.value = newVal;
                    }
                });
            },
            immediate: false,
        },
        isMobile: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    if (this.scrollContainer) {
                        this.scrollContainer.removeEventListener('scroll', this.handleScroll);
                        this.scrollContainer = null;
                    }

                    this.$nextTick(() => {
                        this.setupScrollListener();
                        this.calculateVisibleItems();
                    });
                }
            },
            immediate: false,
        },
        tablePreferences: {
            handler() {
                this.$nextTick(() => {
                    this.$forceUpdate();
                });
            },
            deep: true,
            immediate: false,
        },
    },
    mounted() {
        this.handleResize = this.debounce(() => {
            this.$forceUpdate();
            this.getTableMinWidth();
        }, 150);

        this.setupScrollListener();

        this.calculateColumnWidths();

        this.$nextTick(() => {
            this.calculateVisibleItems();
        });

        this.$nextTick(() => {
            this.addTableRowHoverEvents();
            this.extractTitleFromSlot();
        });

        if (this.showColumnToggler) {
            this.loadTablePreferences();
        }
    },
    methods: {
        setupScrollListener() {
            const scrollContainer = this.findScrollContainer();
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', this.handleScroll, { passive: true });
                this.scrollContainer = scrollContainer;
            } else {
                this.scrollContainer = null;
            }
        },

        handleExport(type, selectedOption) {
            if (type === 'csv') {
                this.exportToCSV();
            }

            this.$emit('export', {
                type,
                selectedOption,
                items: this.filteredItems,
                columns: this.visibleColumns,
            });
        },

        getRowBorderColor(item) {
            const borderColors = [];

            if (item.hasWarning || item.isFailedTrip) {
                borderColors.push('#dc3545');
            }

            if (item.isFlatRate || item.isFlatrateTrip) {
                borderColors.push('#342f54');
            }

            if (item.hasGpsData || item.hasGps) {
                borderColors.push('var(--color-green)');
            }

            return borderColors.length === 0
                ? null
                : borderColors.length === 1
                  ? borderColors[0]
                  : borderColors;
        },

        getTableTitle() {
            if (this.tableTitle) {
                return this.tableTitle;
            }

            if (this.extractedTitle) {
                return this.extractedTitle;
            }

            const titleElement = this.$el?.querySelector(
                '.BaseDashboardTable-title h3, .BaseDashboardTable-title h2, .BaseDashboardTable-title h1',
            );
            if (titleElement) {
                return titleElement.textContent?.trim();
            }

            return null;
        },
        exportToCSV() {
            const headers = this.visibleColumns.map((col) => col.label).join(',');
            const csvContent = this.filteredItems
                .map((item) => {
                    return this.visibleColumns
                        .map((col) => {
                            const value = this.getItemValue(item, col);
                            return `"${String(value || '').replace(/"/g, '""')}"`;
                        })
                        .join(',');
                })
                .join('\n');

            const blob = new Blob([`${headers}\n${csvContent}`], {
                type: 'text/csv;charset=utf-8;',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            const dateStr = new Date().toISOString().split('T')[0];
            let filename = `table-export-${dateStr}`;

            const title = this.getTableTitle();
            if (title) {
                const sanitizedTitle = title
                    .toLowerCase()
                    .replace(/[^a-z0-9äöüß]+/g, '-')
                    .replace(/^-+|-+$/g, '');
                if (sanitizedTitle) {
                    filename = `${sanitizedTitle}-${dateStr}`;
                }
            }
            a.download = `${filename}.csv`;

            a.click();
            window.URL.revokeObjectURL(url);
        },

        findScrollContainer() {
            if (this.isMobile) {
                const tableContainer = this.$refs.tableContainer;
                if (tableContainer && tableContainer.scrollHeight > tableContainer.clientHeight) {
                    return tableContainer;
                }

                return window;
            }

            const selectors = [
                '.Wrap > span',
                '.Wrap',
                '.BaseDashboardTable-container',
                this.$refs.tableContainer,
            ];

            for (const selector of selectors) {
                let container;
                if (typeof selector === 'string') {
                    container = document.querySelector(selector);
                } else {
                    container = selector;
                }

                if (
                    container &&
                    (container.scrollHeight > container.clientHeight ||
                        container.scrollWidth > container.clientWidth)
                ) {
                    return container;
                }
            }

            return window;
        },

        calculateVisibleItems() {
            if (!this.shouldUseVirtualization) {
                this.startIndex = 0;
                this.endIndex = this.filteredItems.length;
                return;
            }

            const scrollContainer = this.scrollContainer || this.findScrollContainer();
            if (!scrollContainer) {
                return;
            }

            if (this.isMobile) {
                this.visibleHeight = window.innerHeight;
            } else {
                this.visibleHeight = window.innerHeight;
            }

            if (this.visibleHeight < this.itemHeight) {
                this.visibleHeight = this.itemHeight;
            }

            const visibleItemCount =
                Math.ceil(this.visibleHeight / this.itemHeight) + this.bufferSize * 2;

            let scrollTop;
            if (scrollContainer === window) {
                scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            } else {
                scrollTop = scrollContainer.scrollTop;
            }

            const startIndex = Math.max(
                0,
                Math.floor(scrollTop / this.itemHeight) - this.bufferSize,
            );
            const endIndex = Math.min(this.filteredItems.length, startIndex + visibleItemCount);

            this.startIndex = startIndex;
            this.endIndex = endIndex;
            this.scrollTop = scrollTop;
        },

        handleScroll(event) {
            if (!this.shouldUseVirtualization) return;

            const now = Date.now();
            if (now - this.lastScrollTime > this.scrollThrottleTime) {
                this.lastScrollTime = now;
                this.recentScrollTime = now;

                let scrollTop;
                if (event.target === window) {
                    scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                } else {
                    scrollTop = event.target.scrollTop;
                }

                this.calculateVisibleItems();

                this.$nextTick(() => {
                    const actualRows = this.$el.querySelectorAll(
                        'tbody tr:not(.top-spacer):not(.bottom-spacer)',
                    );

                    this.addTableRowHoverEvents();
                });
            }
        },

        handleResize() {
            this.$super('handleResize');

            this.debouncedResizeHandler();

            this.$nextTick(() => {
                this.calculateVisibleItems();
                this.addTableRowHoverEvents();
            });
        },

        debouncedResizeHandler: function () {
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }

            this.resizeTimeout = setTimeout(() => {
                this.calculateColumnWidths();
                this.calculateVisibleItems();
                this.$forceUpdate();
            }, 150);
        },
        debounce(fn, delay) {
            let timeoutId;
            return function (...args) {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => fn.apply(this, args), delay);
            };
        },

        throttle(fn, delay = 16) {
            let lastCall = 0;
            return function (...args) {
                const now = Date.now();
                if (now - lastCall >= delay) {
                    fn.apply(this, args);
                    lastCall = now;
                }
            };
        },

        getColumnStyle(column, item) {
            const style = {};

            if (this.useHierarchicalRows) {
                const columnWidth = this.getColumnMinWidth(column);
                style.minWidth = `${columnWidth}px`;

                if (
                    column.width &&
                    typeof column.width === 'string' &&
                    column.width.endsWith('%')
                ) {
                    style.width = column.width;
                } else {
                    style.maxWidth = `${columnWidth * 2}px`;
                }
            } else {
                const columnWidth = this.getColumnMinWidth(column);
                style.width = `${columnWidth}px`;
                style.minWidth = `${columnWidth}px`;
            }

            if (this.isMobile) {
                style.maxWidth = 'unset';
                style.minWidth = 'unset';
                style.width = 'unset';
            }

            if (column.align === 'right') {
                style.whiteSpace = 'nowrap';
            }

            return style;
        },

        isSelected(item) {
            return this.selectedFn(item);
        },

        handleHeaderClick(column) {
            if (column.sortable === false) return;

            if (this.currentSort.column === column.key) {
                this.currentSort.ascending = !this.currentSort.ascending;
            } else {
                this.currentSort.column = column.key;
                this.currentSort.ascending = true;
            }

            this.$emit('sort-changed', { ...this.currentSort });
        },

        handleRowClick(item, index, event) {
            if (item._isGap) return;

            if (!this.isClickable) return;

            const isInteractiveElement = event.target.closest('button, input, a, [role="button"]');
            const isDriverPopover = event.target.closest('.driver-popover-trigger');
            const isLicensePopover = event.target.closest('.license-popover-trigger');

            if (isInteractiveElement && !isInteractiveElement.classList.contains('expand-toggle')) {
                return;
            }

            if (isDriverPopover || isLicensePopover) {
                return;
            }

            this.$emit('row-click', { item, index, event });
        },

        handleRowHover(item, index) {
            this.$emit('row-hover', { item, index });
        },

        handleRowLeave(item, index) {
            this.$emit('row-leave', { item, index });
        },
        handleTableRowHover(event) {
            if (this.isMobile) return;

            const hoverIndicator = this.$refs.tableHoverIndicator;
            if (!hoverIndicator) return;

            const now = Date.now();
            const timeSinceLastScroll = now - this.recentScrollTime;
            const shouldDelay = timeSinceLastScroll < this.hoverDelayAfterScroll;

            if (shouldDelay) {
                this.tableHoverTimeout = setTimeout(() => {
                    hoverIndicator.showTableRowHover(event.currentTarget);
                }, this.hoverDelayAfterScroll - timeSinceLastScroll);
            } else {
                hoverIndicator.showTableRowHover(event.currentTarget);
            }
        },
        handleTableRowLeave() {
            if (this.isMobile) return;

            if (this.tableHoverTimeout) {
                clearTimeout(this.tableHoverTimeout);
                this.tableHoverTimeout = null;
            }

            const hoverIndicator = this.$refs.tableHoverIndicator;
            if (hoverIndicator) {
                hoverIndicator.hide();
            }
        },

        addTableRowHoverEvents() {
            if (this.isMobile) return;

            const tableRows = this.$el.querySelectorAll(
                'tbody tr:not(.top-spacer):not(.bottom-spacer)',
            );

            tableRows.forEach((row, index) => {
                row.removeEventListener('mouseenter', this.handleTableRowHover);
                row.removeEventListener('mouseleave', this.handleTableRowLeave);

                row.addEventListener('mouseenter', this.handleTableRowHover);
                row.addEventListener('mouseleave', this.handleTableRowLeave);
            });
        },

        getItemValue(item, column) {
            let value;

            if (column.key.includes('.')) {
                const keys = column.key.split('.');
                value = item;
                for (const key of keys) {
                    if (value === null || value === undefined) return '';
                    value = value[key];
                }
            } else if (column.formatter && typeof column.formatter === 'function') {
                value = column.formatter(item[column.key], item);
            } else {
                value = item[column.key];
            }

            return value;
        },

        truncateText(text, maxLength = 30) {
            if (text === null || text === undefined) return '';

            const stringValue = String(text);

            if (stringValue.length <= maxLength) return stringValue;

            return stringValue.substring(0, maxLength - 3) + '...';
        },

        getColumnTooltip(item, column) {
            const value = this.getItemValue(item, column);

            if (value !== null && value !== undefined) {
                const stringValue = String(value);
                if (stringValue.length > this.cellTruncateLength) {
                    return stringValue;
                }
            }

            if (column.tooltip) {
                if (typeof column.tooltip === 'function') {
                    return column.tooltip(item);
                }
                return column.tooltip;
            }

            return null;
        },

        isLongContent(content) {
            if (typeof content === 'string') {
                return content.length > 15;
            }
            return false;
        },

        isChecked(item) {
            if (this.itemKey) {
                return this.localCheckedItems.some((i) => i[this.itemKey] === item[this.itemKey]);
            }
            return this.localCheckedItems.includes(item);
        },

        toggleCheck(item) {
            let updatedItems;

            if (this.isChecked(item)) {
                if (this.itemKey) {
                    updatedItems = this.localCheckedItems.filter(
                        (i) => i[this.itemKey] !== item[this.itemKey],
                    );
                } else {
                    updatedItems = this.localCheckedItems.filter((i) => i !== item);
                }
            } else {
                updatedItems = [...this.localCheckedItems, item];
            }

            this.localCheckedItems = updatedItems;
            this.$emit('checked-changed', updatedItems);
        },

        toggleSelectAll() {
            if (this.allSelected) {
                this.localCheckedItems = [];
            } else {
                this.localCheckedItems = [...this.items];
            }
            this.$emit('checked-changed', this.localCheckedItems);
        },

        resolveComponent(component) {
            if (typeof component === 'string') {
                if (this.globalComponents && this.globalComponents[component]) {
                    return this.globalComponents[component];
                }

                if (this.$root.$options.components && this.$root.$options.components[component]) {
                    return component;
                }

                const parentComponents = this.$parent.$options.components || {};
                if (parentComponents[component]) {
                    return component;
                }

                return 'div';
            }

            return component;
        },

        getComponentProps(column, item) {
            const defaultProps = {
                value: this.getItemValue(item, column),
                item: item,
                column: column,
            };

            if (column.props) {
                if (typeof column.props === 'function') {
                    return { ...defaultProps, ...column.props(item) };
                }
                return { ...defaultProps, ...column.props };
            }

            return defaultProps;
        },

        isRowExpanded(item) {
            return item._expanded === true;
        },

        hasChildren(item) {
            return (item.children && item.children.length > 0) || item._hasChildren === true;
        },

        toggleExpand(item, index) {
            item._expanded = !item._expanded;
            this.$emit('toggle-expand', { item, index, expanded: item._expanded });
        },

        toggleItemChecked(item) {
            if (!this.selectable) return;
            this.toggleCheck(item);
        },

        isColumnVisible(column) {
            if (!this.tablePreferences) return true;
            const visibilityMap = this.tablePreferences.columns?.visibility || {};
            return visibilityMap[column.key] !== false;
        },

        isColumnRequired(column) {
            if (!this.tablePreferences) return false;
            const defaultVisible = this.tablePreferences.defaultVisible || [];
            return defaultVisible.includes(column.key);
        },

        async loadTablePreferences() {
            try {
                await this.$store.dispatch('tablePreferences/loadGlobalPreferences');
            } catch (error) {
                console.warn(
                    'Failed to load global table preferences from server, using defaults:',
                    error,
                );
            }
        },

        async toggleColumnVisibility(column, close) {
            if (this.isColumnRequired(column)) return;

            const isVisible = this.isColumnVisible(column);

            const currentPreferences = this.tablePreferences || {
                columns: { visibility: {} },
                defaultVisible: [],
            };

            const updatedVisibility = {
                ...currentPreferences.columns.visibility,
                [column.key]: !isVisible,
            };

            const updatedPreferences = {
                ...currentPreferences,
                columns: {
                    ...currentPreferences.columns,
                    visibility: updatedVisibility,
                },
            };

            try {
                await this.$store.dispatch('tablePreferences/updateTablePreferences', {
                    tableId: this.tableId,
                    preferences: updatedPreferences,
                });
            } catch (error) {
                console.warn(
                    'Failed to save column visibility preferences, changes applied locally:',
                    error,
                );
            }
        },

        getTableMinWidth() {
            if (this.isMobile) {
                this.tableMinWidth = '100%';
                return;
            }

            let minWidth = 0;

            this.visibleColumns.forEach((column) => {
                let columnWidth = this.getColumnMinWidth(column);
                minWidth += columnWidth;
            });

            minWidth += 20;

            const availableSpace = this.$refs.tableContainer
                ? this.$refs.tableContainer.clientWidth
                : 0;

            this.tableMinWidth = `${Math.max(minWidth, availableSpace - 10)}px`;
        },

        getColumnMinWidth(column) {
            if (column.key === 'driverName' || column.key === 'driver' || column.key === 'name') {
                return this.hasAnyExpandableRows ? 230 : 200;
            }

            if (column.width) {
                if (typeof column.width === 'string') {
                    if (column.width.endsWith('px')) {
                        return parseInt(column.width, 10);
                    } else if (column.width.endsWith('%')) {
                        return 80;
                    }
                }
            }

            if (this.maxColumnWidths[column.key]) {
                return Math.max(this.maxColumnWidths[column.key], 80);
            }

            return 80;
        },

        extractTitleFromSlot() {
            this.$nextTick(() => {
                const titleElement = this.$el?.querySelector(
                    '.BaseDashboardTable-title h3, .BaseDashboardTable-title h2, .BaseDashboardTable-title h1',
                );
                if (titleElement) {
                    this.extractedTitle = titleElement.textContent?.trim();
                }
            });
        },
        calculateColumnWidths() {
            if (this.isMobile) {
                this.getTableMinWidth();
                return;
            }

            if (this.items.length > 1000) {
                this.columns.forEach((column) => {
                    if (!this.maxColumnWidths[column.key]) {
                        this.maxColumnWidths[column.key] = 120;
                    }
                });
                this.getTableMinWidth();
                return;
            }

            const tempContainer = document.createElement('div');
            tempContainer.style.cssText = `
                position: absolute;
                visibility: hidden;
                white-space: nowrap;
                font-family: inherit;
                font-size: 0.8rem;
                padding: 4px 8px;
                box-sizing: border-box;
            `;
            document.body.appendChild(tempContainer);

            this.columns.forEach((column) => {
                let maxWidth = 0;

                const headerText = column.label || '';
                tempContainer.textContent = headerText;
                const headerWidth = tempContainer.offsetWidth;
                maxWidth = Math.max(maxWidth, headerWidth);

                const itemsToMeasure = this.useHierarchicalRows ? this.filteredItems : this.items;
                const sampleSize = Math.min(100, itemsToMeasure.length);
                const sampleItems = itemsToMeasure.slice(0, sampleSize);

                sampleItems.forEach((item) => {
                    const value = this.getItemValue(item, column);
                    if (value !== null && value !== undefined) {
                        const text = this.truncateText(String(value), this.cellTruncateLength);
                        tempContainer.textContent = text;
                        const contentWidth = tempContainer.offsetWidth;
                        maxWidth = Math.max(maxWidth, contentWidth);
                    }
                });

                this.maxColumnWidths[column.key] = maxWidth + 16;
            });

            document.body.removeChild(tempContainer);

            this.getTableMinWidth();
        },
    },
    beforeDestroy() {
        if (this.tableHoverTimeout) {
            clearTimeout(this.tableHoverTimeout);
        }

        const tableRows = this.$el.querySelectorAll('tbody tr');
        tableRows.forEach((row) => {
            row.removeEventListener('mouseenter', this.handleTableRowHover);
            row.removeEventListener('mouseleave', this.handleTableRowLeave);
        });

        if (this.scrollContainer) {
            this.scrollContainer.removeEventListener('scroll', this.handleScroll);
        }
    },
};
</script>

<style lang="scss">
.BaseDashboardTable {
    display: flex;
    flex-direction: column;
    width: 100%;
    font-size: 0.85rem;
    padding: 4px;
    height: 100%;
    position: relative;

    &-header {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 12px 16px;
        border-bottom: 0.5px solid rgba(0, 0, 0, 0.08);

        @media screen and (min-width: $breakpoint-tablet-portrait) {
            gap: 8px;
            padding: 8px 12px;
        }

        .BaseDashboardTable-title-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
        }

        .BaseDashboardTable-title {
            margin: 0;
            font-size: 0.9rem;
            font-weight: 400;
            color: var(--color-text-black);
            white-space: normal;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
            flex: 1;

            @media screen and (min-width: $breakpoint-tablet-portrait) {
                font-size: 0.75rem;
            }

            .item-counter {
                font-size: 0.8rem;
                font-weight: 400;
                color: var(--color-gray);
                padding: 0px 12px;
                border-radius: 12px;
                white-space: nowrap;
                background: var(--color-bg);

                @media screen and (min-width: $breakpoint-tablet-portrait) {
                    font-size: 0.7rem;
                    padding: 0px 8px;
                }
            }
        }

        .BaseDashboardTable-actions {
            display: flex;
            gap: 8px;
            justify-content: flex-end;
            flex-shrink: 0;

            @media screen and (min-width: $breakpoint-tablet-portrait) {
                gap: 4px;
            }
        }

        .search-container {
            position: relative;
            width: 100%;
            flex-shrink: 0;

            @media screen and (min-width: $breakpoint-tablet-portrait) {
                width: auto;
                max-width: 300px;
            }
        }

        .search-legend-container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            padding: 8px 0px;

            @media screen and (max-width: $breakpoint-tablet-portrait) {
                flex-direction: column;
                align-items: stretch;
                gap: 8px;
            }
        }

        .search-legend-left {
            display: flex;
            align-items: center;
            gap: 16px;
        }

        &-legend {
            flex-shrink: 0;
            margin: 0;
        }

        .search-icon {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: var(--color-gray);
            font-size: 1.1rem;

            @media screen and (min-width: $breakpoint-tablet-portrait) {
                left: 8px;
                font-size: 1rem;
            }
        }

        .search-input {
            width: 100%;
            padding: 12px 16px 12px 44px;
            border: 0.5px solid rgba(0, 0, 0, 0.15);
            border-radius: 8px;
            font-size: 1rem;
            color: var(--color-text-black);
            background-color: var(--color-white);
            transition: all 0.2s ease;
            -webkit-appearance: none;
            appearance: none;

            @media screen and (min-width: $breakpoint-tablet-portrait) {
                width: 300px;
                padding: 6px 8px 6px 32px;
                border-radius: 4px;
                font-size: 0.85rem;
            }

            &:focus {
                outline: none;
                border-color: var(--color-blue);
                box-shadow: 0 0 0 3px rgba(var(--color-blue-rgb), 0.2);

                @media screen and (min-width: $breakpoint-tablet-portrait) {
                    box-shadow: 0 0 0 2px rgba(var(--color-blue-rgb), 0.6);
                }
            }

            &::placeholder {
                color: rgba(var(--color-blue-rgb), 0.7);
            }
        }
    }

    &-container {
        position: relative;
        flex: 1;
        min-height: 100px;
        display: flex;
        flex-direction: column;
        will-change: contents;
        overflow-y: auto;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        z-index: 1;

        &.no-items {
            flex: none;
            min-height: auto;
        }

        .placeholder-row {
            transition: height 0.15s ease;
        }

        table {
            table-layout: fixed;
            border-collapse: collapse;

            &.hierarchical-table {
                table-layout: auto;
                min-width: 100%;
            }
            thead {
                position: sticky;
                top: 0;
                background-color: var(--color-white);
                z-index: 9;

                &::after {
                    content: '';
                    position: absolute;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    height: 1px;
                    background-color: rgba(0, 0, 0, 0.1);
                }

                tr {
                    background-color: var(--color-white);
                    th {
                        padding: 8px;
                        font-size: 0.8rem;
                        font-weight: 400;
                        color: var(--color-gray);
                        text-align: left;
                        white-space: nowrap;
                        overflow: visible;
                        width: 100%;

                        &.align-right,
                        &.text-right {
                            text-align: right;
                        }

                        &.align-center,
                        &.text-center {
                            text-align: center;
                        }

                        &.sortable {
                            cursor: pointer;
                            user-select: none;

                            &:hover {
                                color: var(--color-text-black);
                                background-color: rgba(0, 0, 0, 0.02);
                            }
                        }

                        &.sorted {
                            color: var(--color-text-blue-dark);
                        }

                        .column-header {
                            display: flex;
                            justify-content: flex-start;
                            align-items: center;

                            i {
                                margin-left: 4px;
                                opacity: 0.3;
                                font-size: 0.9rem;
                                transition: all 0.2s ease;

                                &.active {
                                    opacity: 1;
                                    color: var(--color-blue);
                                }
                            }
                        }

                        &.sortable:hover .column-header i {
                            opacity: 1;
                            color: var(--color-blue-dark);
                        }

                        &.text-right .column-header {
                            justify-content: flex-end;

                            i {
                                margin-left: 0;
                                margin-right: 4px;
                                order: -1;
                            }
                        }

                        &.text-center .column-header {
                            justify-content: center;

                            i {
                                margin-left: 4px;
                            }
                        }

                        &.sticky-column {
                            @media screen and (max-width: $breakpoint-tablet-portrait) {
                                position: sticky;
                                left: 0;
                                z-index: 11;
                                background-color: var(--color-white);

                                &::after {
                                    content: '';
                                    position: absolute;
                                    right: 0;
                                    top: 0;
                                    bottom: 0;
                                    width: 1px;
                                    background-color: rgba(0, 0, 0, 0.1);
                                    pointer-events: none;
                                }
                            }
                        }
                    }
                }
            }

            tbody {
                tr {
                    border-bottom: 0px solid rgba(0, 0, 0, 0);
                    border-left: 0.5px solid transparent;

                    background-color: var(--color-white);

                    &.row-even {
                        background-color: rgb(245 245 245);
                    }

                    &.clickable {
                        cursor: pointer;
                    }

                    &.selected {
                        background-color: #dfe1ee;
                        border-left: 0.5px solid var(--color-blue);
                    }

                    &.row-even {
                        &:hover {
                        }

                        &.selected {
                            background-color: #e3dfee;
                        }
                    }

                    &.parent-row {
                        font-weight: 500;
                        transform: translate(0);
                        clip-path: inset(0);

                        &.expanded {
                            border-left: 0.5px solid var(--color-blue);
                            background-color: #cdced6;
                            color: var(--color-text-white) !important;

                            &::after {
                                display: none;
                            }
                        }
                    }

                    td {
                        padding: 4px 8px;
                        font-size: 0.8rem;
                        color: var(--color-text-black);
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        min-width: 50px;

                        &.text-right {
                            > * {
                                text-align: right;
                                display: inline-flex;
                                justify-content: flex-end;
                            }
                        }
                        &.text-left {
                            > * {
                                text-align: left;
                                display: inline-flex;
                                justify-content: flex-start;
                            }
                        }
                        &.text-center {
                        }
                        > span {
                            overflow: hidden;
                            text-overflow: ellipsis;
                            min-width: 50px;
                        }

                        &.first-column {
                            display: flex;
                            align-items: center;
                            gap: 4px;

                            .expand-toggle,
                            .expand-toggle-spacer {
                                display: inline-flex;
                                align-items: center;
                                justify-content: center;
                                min-width: 20px;
                                width: 20px;
                                height: 20px;
                                margin-right: 4px;
                                flex-shrink: 0;
                            }

                            .expand-toggle {
                                color: var(--color-blue);
                                cursor: pointer;
                                border-radius: 2px;
                                transition: all 0.2s ease;

                                &:hover {
                                    background-color: rgba(var(--color-blue-rgb), 0.1);
                                    transform: scale(1.1);

                                    i {
                                        opacity: 1;
                                        color: var(--color-blue);
                                    }
                                }

                                i {
                                    font-size: 18px;
                                    display: block;
                                    opacity: 0.7;
                                    transition: all 0.2s ease;
                                }
                            }
                        }

                        &.indented {
                            .indentation {
                                display: inline-block;
                                width: calc(20px + 4px);
                                margin-right: 4px;
                            }

                            padding-left: calc(8px + (var(--indent-level, 0) * 24px));
                        }

                        &.align-right {
                            text-align: right;
                            font-feature-settings: 'tnum';
                            font-variant-numeric: tabular-nums;
                            padding-right: 10px;
                        }

                        &.align-center {
                            text-align: center;
                        }

                        &.has-long-content {
                            position: relative;
                        }

                        .child-row & {
                            white-space: normal;
                            line-height: 1.3;

                            &.date-column {
                                white-space: nowrap;
                            }
                        }
                    }
                }

                .nested-table-row {
                    background-color: rgba(var(--color-blue-rgb), 0.02);

                    td {
                        padding: 0;
                    }
                }
            }
        }
    }
    .top-spacer,
    .bottom-spacer {
        min-height: 1px;
        pointer-events: none;
        background-color: transparent !important;
        border: none;

        &:hover {
            background-color: transparent !important;
            border-left-color: transparent;
        }

        &.row-even {
            background-color: transparent !important;
        }
    }
    .table-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 20px;
        color: var(--color-gray);

        i {
            font-size: 1.8rem;
            margin-bottom: 8px;
            opacity: 0.5;
        }

        p {
            font-size: 0.8rem;
            font-style: italic;
            margin: 0;
        }
    }

    @media screen and (max-width: $breakpoint-tablet-portrait) {
        &-container {
            overflow-y: auto !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
            max-height: none;

            table {
                tbody {
                    tr {
                        &.child-row {
                            &::before,
                            &::after {
                                display: none !important;
                            }
                        }
                    }
                }

                td {
                    padding: 8px;

                    .expand-toggle {
                        min-width: 28px;
                        width: 28px;
                        height: 28px;
                    }
                }
            }
        }

        &-header {
        }
    }

    @media screen and (max-width: $breakpoint-mobile) {
        &-container table {
            td {
                padding: 8px 4px !important;
            }
        }

        &-header {
        }
    }

    @media screen and (max-width: $breakpoint-mobile) {
        &-container table {
            th,
            td {
                width: auto !important;
                min-width: auto !important;
                max-width: none !important;
            }

            th[data-priority='2'],
            th[data-priority='3'],
            th[data-priority='4'],
            th[data-priority='5'] {
                display: none !important;
            }

            td[data-priority='2'],
            td[data-priority='3'],
            td[data-priority='4'],
            td[data-priority='5'] {
                display: none !important;
            }
        }
    }

    @media screen and (max-width: $breakpoint-tablet-portrait) and (min-width: $breakpoint-mobile) {
        &-container table {
            th,
            td {
                width: auto !important;
                min-width: auto !important;
                max-width: none !important;
            }

            th[data-priority='3'],
            th[data-priority='4'],
            th[data-priority='5'] {
                display: none !important;
            }

            td[data-priority='3'],
            td[data-priority='4'],
            td[data-priority='5'] {
                display: none !important;
            }
        }
    }

    @media screen and (max-width: $breakpoint-tablet-landscape) and (min-width: $breakpoint-tablet-portrait) {
        &-container table {
            th[data-priority='4'],
            th[data-priority='5'] {
                display: none !important;
            }

            td[data-priority='4'],
            td[data-priority='5'] {
                display: none !important;
            }
        }
    }

    .table-container table {
        td[data-priority='high'],
        th[data-priority='high'] {
            min-width: 80px;

            @media screen and (max-width: $breakpoint-tablet-portrait) {
                min-width: auto;
            }
        }
    }

    &.compact-mode {
        &-header {
            padding: 4px 6px;

            &-title {
                font-size: 0.85rem;
            }
        }

        thead th {
            padding: 4px 6px;
            font-size: 0.7rem;
        }

        tbody td {
            padding: 3px 6px;
            font-size: 0.75rem;
        }
    }

    .placeholder-row {
        display: table-row;
        height: 35px;
        border: none;
        pointer-events: none;
        opacity: 0;

        &:nth-child(odd),
        &:nth-child(even) {
            background-color: transparent !important;
        }
    }

    .counter-column {
    }

    .column-toggler {
        .column-toggler-button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border: 0.5px solid rgba(0, 0, 0, 0.15);
            border-radius: 4px;
            background-color: var(--color-white);
            color: var(--color-gray);
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                background-color: rgba(0, 0, 0, 0.05);
                border-color: var(--color-blue);
                color: var(--color-blue);
            }

            i {
                font-size: 1.1rem;
            }
        }
    }
}
.column-toggler-content {
    min-width: 200px;

    .column-toggler-header {
        font-size: 0.9rem;
        font-weight: 600;
        color: var(--color-text-black);
        margin-bottom: 12px;
        text-align: center;
    }

    .column-toggler-list {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .column-toggler-item {
            padding: 4px 0;
        }
    }
}
</style>
